import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, Mail, AlertTriangle, Clock, FileText, CheckCircle, ShieldOff } from 'lucide-react';
import { BRAND } from '../brand';
import api from '../utils/api';
import { BiometricRevokeConsequences } from '../legal/biometricConsent';

const Step = ({ num, title, children }) => (
  <div className="flex gap-3 mb-4">
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center font-bold text-sm">
      {num}
    </div>
    <div className="flex-1">
      <h3 className="font-bold text-gray-900 text-base mb-1">{title}</h3>
      <div className="text-sm text-gray-700 leading-relaxed">{children}</div>
    </div>
  </div>
);

const DataRow = ({ icon: Icon, label, retention, color }) => (
  <div className={`flex items-start gap-3 p-3 rounded-xl border ${color}`}>
    <Icon size={18} className="flex-shrink-0 mt-0.5" />
    <div className="flex-1">
      <p className="font-semibold text-sm text-gray-900">{label}</p>
      <p className="text-xs text-gray-600 mt-0.5">{retention}</p>
    </div>
  </div>
);

const EliminarCuenta = () => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [revokeResult, setRevokeResult] = useState(null);
  const [revokeError, setRevokeError] = useState('');
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);
  const [reason, setReason] = useState('');

  const isAuthed = !!localStorage.getItem('token');

  const submitRevoke = async () => {
    setRevoking(true);
    setRevokeError('');
    try {
      const r = await api.post('/biometric/consent/revoke', {
        confirm: true,
        channel: 'eliminar_cuenta_page',
        reason: reason.trim() || null,
      });
      setRevokeResult(r.data);
      setConfirmingRevoke(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setRevokeError(d?.message || d || 'No se pudo revocar el consentimiento.');
    } finally {
      setRevoking(false);
    }
  };

  const supportEmail = `${BRAND.supportEmail}`;
  const mailtoLink = `mailto:${supportEmail}?subject=Solicitud%20de%20eliminaci%C3%B3n%20de%20cuenta%20${BRAND.name}&body=Hola%20equipo%20${BRAND.name}%2C%0A%0ASolicito%20la%20eliminaci%C3%B3n%20de%20mi%20cuenta%20y%20datos%20asociados.%0A%0AEmail%20registrado%3A%20%5BTu%20email%5D%0ANombre%20completo%3A%20%5BTu%20nombre%5D%0ARUT%20o%20CI%3A%20%5BTu%20documento%5D%0A%0AConfirmo%20que%20entiendo%20que%3A%0A-%20Mi%20cuenta%20ser%C3%A1%20eliminada%20de%20forma%20permanente%0A-%20Los%20datos%20relacionados%20a%20transacciones%20completadas%20se%20conservar%C3%A1n%20por%205%20a%C3%B1os%20por%20obligaci%C3%B3n%20legal%20(UAF%2FASFI)%0A%0AGracias.`;

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(supportEmail);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      setCopied(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 text-sm"
          data-testid="back-home-btn"
        >
          <ArrowLeft size={16} /> Volver al inicio
        </button>

        <div className="bg-white rounded-3xl shadow-xl border p-6 sm:p-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <Trash2 size={24} className="text-red-600" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Eliminar mi cuenta
              </h1>
              <p className="text-xs text-gray-500 mt-1">{BRAND.name} - Solicitud de eliminación de datos</p>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6 flex gap-3">
            <AlertTriangle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-900">
              <p className="font-bold mb-1">Esta acción es permanente</p>
              <p>
                Una vez eliminada tu cuenta de <strong>{BRAND.name}</strong>, no podrás recuperar tu historial,
                {' '}{BRAND.puntosProgramName}, ni destinatarios guardados. Asegúrate de descargar tus comprobantes en PDF antes de solicitar la eliminación.
              </p>
            </div>
          </div>

          {/* ─────────────────────────────────────────────────────────
              Revocación del consentimiento biométrico (Ley 21.719)
              Camino self-service, distinto de eliminar la cuenta entera.
              ───────────────────────────────────────────────────────── */}
          {isAuthed && !revokeResult && (
            <section className="mb-8 rounded-2xl border-2 border-slate-200 p-4 sm:p-5" data-testid="revoke-biometric-block">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                  <ShieldOff size={20} className="text-slate-700" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Revocar el consentimiento biométrico</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Ley 21.719 · No borra tu cuenta, sólo bloquea el uso de tu biometría.</p>
                </div>
              </div>
              <div className="mb-3">
                <BiometricRevokeConsequences brandName={BRAND.name} />
              </div>
              {!confirmingRevoke ? (
                <button
                  onClick={() => setConfirmingRevoke(true)}
                  className="text-sm font-bold px-4 py-2 rounded-lg bg-slate-800 text-white hover:bg-slate-900"
                  data-testid="revoke-biometric-start-btn"
                >
                  Revocar consentimiento biométrico
                </button>
              ) : (
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                  <p className="text-sm text-slate-800 font-semibold mb-1">
                    Antes de confirmar, ten en cuenta:
                  </p>
                  <div className="mb-2">
                    <BiometricRevokeConsequences brandName={BRAND.name} />
                  </div>
                  <p className="text-xs text-slate-600 mb-2">Puedes dejar un motivo (opcional).</p>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Motivo (opcional)"
                    className="w-full text-sm px-3 py-2 border border-slate-300 rounded mb-2"
                    rows={2}
                    data-testid="revoke-biometric-reason"
                  />
                  {revokeError && <p className="text-xs text-red-600 mb-2">{revokeError}</p>}
                  <div className="flex gap-2">
                    <button
                      onClick={submitRevoke}
                      disabled={revoking}
                      className="text-sm font-bold px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                      data-testid="revoke-biometric-confirm-btn"
                    >
                      {revoking ? 'Revocando…' : 'Sí, revocar y bloquear mi cuenta'}
                    </button>
                    <button
                      onClick={() => { setConfirmingRevoke(false); setRevokeError(''); }}
                      className="text-sm px-4 py-2 rounded-lg bg-white border border-slate-300 hover:bg-slate-100"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}
          {revokeResult && (
            <section className="mb-8 rounded-2xl border-2 border-green-200 bg-green-50 p-4 sm:p-5" data-testid="revoke-biometric-result">
              <div className="flex items-start gap-3">
                <CheckCircle size={22} className="text-green-700 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-green-900">
                  <p className="font-bold text-base mb-1">Consentimiento biométrico revocado</p>
                  <p>Dejamos de usar tu biometría a partir de este momento. El dato queda bloqueado por conservación legal.</p>
                  {revokeResult.deletion_scheduled_at && (
                    <p className="mt-1 text-xs">Fecha estimada de eliminación definitiva: <strong>{revokeResult.deletion_scheduled_at}</strong></p>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Pasos */}
          <section className="mb-8">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">
              ¿Cómo solicitar la eliminación?
            </h2>

            <Step num={1} title={`Envíanos un correo a ${BRAND.supportEmail}`}>
              Desde el correo electrónico con el que te registraste en {BRAND.name}, envía un correo a
              <span className="font-semibold"> {BRAND.supportEmail}</span> con el asunto:
              <span className="block bg-gray-100 rounded-md px-2 py-1 mt-2 font-mono text-xs">
                Solicitud de eliminación de cuenta {BRAND.name}
              </span>
            </Step>

            <Step num={2} title="Incluye estos datos en el correo">
              <ul className="list-disc ml-5 space-y-1 mt-1">
                <li>Email registrado en {BRAND.name}</li>
                <li>Nombre completo (igual al de tu KYC)</li>
                <li>RUT (Chile) o Carnet de Identidad (Bolivia)</li>
                <li>Motivo (opcional)</li>
              </ul>
            </Step>

            <Step num={3} title="Verificación de identidad (1-3 días hábiles)">
              Nuestro equipo verificará tu identidad para evitar eliminaciones fraudulentas. Podríamos solicitarte una selfie sosteniendo tu documento.
            </Step>

            <Step num={4} title="Confirmación y eliminación (hasta 30 días)">
              Una vez confirmada tu identidad, recibirás un correo de confirmación. Tu cuenta y los datos no obligatorios serán eliminados en un plazo máximo de 30 días corridos.
            </Step>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <a
                href={mailtoLink}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-4 rounded-xl transition text-sm"
                data-testid="email-delete-btn"
              >
                <Mail size={18} /> Abrir correo de solicitud
              </a>
              <button
                onClick={copyEmail}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-900 font-bold py-3 px-4 rounded-xl transition text-sm"
                data-testid="copy-email-btn"
              >
                {copied ? <CheckCircle size={18} className="text-green-600" /> : <Mail size={18} />}
                {copied ? '¡Email copiado!' : `Copiar ${supportEmail}`}
              </button>
            </div>
          </section>

          {/* Datos que se eliminan */}
          <section className="mb-8">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">
              Datos que se eliminan
            </h2>
            <p className="text-sm text-gray-700 mb-3">
              Al confirmar la eliminación, {BRAND.name} borrará permanentemente:
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <DataRow
                icon={Trash2}
                label="Datos personales (email, teléfono, nombre)"
                retention="Eliminados inmediatamente"
                color="bg-red-50 border-red-200"
              />
              <DataRow
                icon={Trash2}
                label="Documentos KYC (cédula, selfie)"
                retention="Eliminados inmediatamente"
                color="bg-red-50 border-red-200"
              />
              <DataRow
                icon={Trash2}
                label="Destinatarios guardados"
                retention="Eliminados inmediatamente"
                color="bg-red-50 border-red-200"
              />
              <DataRow
                icon={Trash2}
                label={`${BRAND.puntosProgramName} y misiones`}
                retention="Eliminados inmediatamente"
                color="bg-red-50 border-red-200"
              />
              <DataRow
                icon={Trash2}
                label="Preferencias y notificaciones"
                retention="Eliminados inmediatamente"
                color="bg-red-50 border-red-200"
              />
              <DataRow
                icon={Trash2}
                label="Tokens de sesión"
                retention="Eliminados inmediatamente"
                color="bg-red-50 border-red-200"
              />
            </div>
          </section>

          {/* Datos que se conservan */}
          <section className="mb-8">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">
              Datos que se conservan por obligación legal
            </h2>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-3 flex gap-3">
              <FileText size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-900">
                Por normativa <strong>UAF (Chile)</strong> y <strong>ASFI (Bolivia)</strong>, debemos conservar
                ciertos registros financieros aunque elimines tu cuenta. Esto es obligatorio
                y no podemos eliminarlos antes del plazo legal.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <DataRow
                icon={Clock}
                label="Registros de transferencias completadas"
                retention="Se conservan 5 años (Ley UAF / Ley 393 ASFI)"
                color="bg-blue-50 border-blue-200"
              />
              <DataRow
                icon={Clock}
                label="Comprobantes y facturas tributarias"
                retention="Se conservan 6 años (SII Chile)"
                color="bg-blue-50 border-blue-200"
              />
              <DataRow
                icon={Clock}
                label="Logs de auditoría (anonimizados)"
                retention="Se conservan 5 años para auditorías UAF/ASFI"
                color="bg-blue-50 border-blue-200"
              />
              <DataRow
                icon={Clock}
                label="Reportes de denuncias en curso"
                retention="Hasta el cierre del caso (Ley 21.595)"
                color="bg-blue-50 border-blue-200"
              />
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Estos registros quedan <strong>anonimizados</strong> (sin tu nombre ni email visible),
              y solo son accesibles bajo orden judicial o auditoría regulatoria.
            </p>
          </section>

          {/* Tiempo de respuesta */}
          <section className="mb-4">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3">
              Tiempos de respuesta
            </h2>
            <ul className="text-sm text-gray-700 space-y-1 list-disc ml-5">
              <li><strong>Confirmación inicial:</strong> dentro de 48 horas hábiles.</li>
              <li><strong>Verificación de identidad:</strong> 1 a 3 días hábiles.</li>
              <li><strong>Eliminación efectiva de datos:</strong> hasta 30 días corridos.</li>
            </ul>
          </section>

          <div className="border-t pt-4 mt-6 text-center">
            <p className="text-xs text-gray-500">
              Última actualización: febrero 2026 - {BRAND.name} -{' '}
              <button
                onClick={() => navigate('/privacidad')}
                className="text-green-700 underline hover:text-green-900"
                data-testid="privacy-link"
              >
                Política de privacidad completa
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EliminarCuenta;
